import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft, CircleCheck, Circle, Award, ClipboardCheck, Clock3,
  PlayCircle, FileText, Download, ExternalLink, ListChecks, FileDown,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CATEGORY_LABELS } from "@/pages/academy/Academy";

const LESSON_TYPE_META = {
  text: { icon: FileText, de: "Text", en: "Text" },
  video: { icon: PlayCircle, de: "Video", en: "Video" },
  pdf: { icon: FileDown, de: "PDF", en: "PDF" },
  download: { icon: Download, de: "Download", en: "Download" },
  link: { icon: ExternalLink, de: "Externer Link", en: "External link" },
  checklist: { icon: ListChecks, de: "Checkliste", en: "Checklist" },
};

function videoEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

export async function downloadFileById(fileId, fallbackName = "datei") {
  const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
  const disposition = res.headers["content-disposition"] || "";
  const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/* ---------- Lesson content renderers per type ---------- */

function VideoLesson({ lesson, tt }) {
  const embed = videoEmbedUrl(lesson.video_url);
  return (
    <div className="mt-5 space-y-4">
      {embed ? (
        <div className="aspect-video rounded-[14px] overflow-hidden border border-border bg-black">
          <iframe
            src={embed}
            title={tt(lesson.title)}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="lesson-video-iframe"
          />
        </div>
      ) : lesson.video_url ? (
        <video src={lesson.video_url} controls className="w-full rounded-[14px] border border-border" data-testid="lesson-video-native" />
      ) : (
        <p className="text-sm text-muted-foreground">Kein Video hinterlegt.</p>
      )}
      {lesson.content && (
        <div className="text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-line">{tt(lesson.content)}</div>
      )}
    </div>
  );
}

function FileLesson({ lesson, t, tt }) {
  const [busy, setBusy] = useState(false);
  const handleDownload = async () => {
    try {
      setBusy(true);
      if (lesson.file_id) {
        await downloadFileById(lesson.file_id, tt(lesson.title));
      } else if (lesson.file_url) {
        window.open(lesson.file_url, "_blank", "noopener");
      }
    } catch {
      toast.error(t("Download fehlgeschlagen", "Download failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 space-y-4">
      {lesson.content && (
        <div className="text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-line">{tt(lesson.content)}</div>
      )}
      <div className="rounded-[14px] border border-border bg-muted/40 p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <FileDown className="h-8 w-8 text-[#A8905A] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{lesson.file_name || tt(lesson.title)}</p>
            <p className="text-xs text-muted-foreground">{t("Schulungsunterlage", "Training material")}</p>
          </div>
        </div>
        <Button onClick={handleDownload} disabled={busy || (!lesson.file_id && !lesson.file_url)} className="rounded-[12px] bg-[#0B1220] shrink-0" data-testid="lesson-download-button">
          <Download className="h-4 w-4 mr-1.5" /> {t("Herunterladen", "Download")}
        </Button>
      </div>
    </div>
  );
}

function LinkLesson({ lesson, t, tt }) {
  return (
    <div className="mt-5 space-y-4">
      {lesson.content && (
        <div className="text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-line">{tt(lesson.content)}</div>
      )}
      <Button asChild variant="outline" className="rounded-[12px] border-[#D6C08D]/50" data-testid="lesson-external-link">
        <a href={lesson.external_url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-4 w-4 mr-1.5" /> {t("Link \u00f6ffnen", "Open link")}
        </a>
      </Button>
    </div>
  );
}

function ChecklistLesson({ lesson, tt, checked, setChecked, isDone }) {
  const items = lesson.items || [];
  const toggle = (itemId) => {
    setChecked((prev) => prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId]);
  };
  return (
    <div className="mt-5 space-y-4">
      {lesson.content && (
        <div className="text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-line">{tt(lesson.content)}</div>
      )}
      <div className="space-y-2" data-testid="lesson-checklist">
        {items.map((item) => (
          <label
            key={item.id}
            className={`flex items-start gap-3 rounded-[12px] border px-4 py-3 cursor-pointer transition-colors ${
              checked.includes(item.id) ? "border-emerald-500/40 bg-emerald-500/5" : "border-border hover:bg-muted/50"
            }`}
          >
            <Checkbox
              checked={isDone || checked.includes(item.id)}
              disabled={isDone}
              onCheckedChange={() => toggle(item.id)}
              className="mt-0.5"
              data-testid={`checklist-item-${item.id}`}
            />
            <span className="text-sm">
              {tt(item.label)}
              {item.required !== false && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#A8905A] font-semibold">Pflicht</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ---------- Quiz ---------- */

function QuizQuestion({ q, qi, answer, setAnswer, tt, result }) {
  const qtype = q.qtype || "single";
  const fb = result?.per_question?.find((p) => p.question_id === q.id);
  return (
    <div className={`rounded-[14px] border p-5 ${fb ? (fb.correct ? "border-emerald-500/40" : fb.counts ? "border-red-500/40" : "border-border") : "border-border"}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium">{qi + 1}. {tt(q.question)}</p>
        {qtype === "multiple" && <Badge variant="secondary" className="rounded-full text-[10px] shrink-0">Mehrfachauswahl</Badge>}
        {qtype === "text" && <Badge variant="secondary" className="rounded-full text-[10px] shrink-0">Freitext</Badge>}
      </div>
      {qtype === "single" && (
        <RadioGroup value={answer?.toString() ?? ""} onValueChange={(v) => setAnswer(parseInt(v))} className="mt-3 space-y-2">
          {q.options.map((opt, oi) => (
            <label key={oi} className="flex items-center gap-3 rounded-[10px] border border-border px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
              <RadioGroupItem value={oi.toString()} data-testid={`quiz-q${qi}-opt${oi}`} />
              <span className="text-sm">{tt(opt)}</span>
            </label>
          ))}
        </RadioGroup>
      )}
      {qtype === "multiple" && (
        <div className="mt-3 space-y-2">
          {q.options.map((opt, oi) => {
            const arr = Array.isArray(answer) ? answer : [];
            return (
              <label key={oi} className="flex items-center gap-3 rounded-[10px] border border-border px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                <Checkbox
                  checked={arr.includes(oi)}
                  onCheckedChange={(c) => setAnswer(c ? [...arr, oi] : arr.filter((x) => x !== oi))}
                  data-testid={`quiz-q${qi}-opt${oi}`}
                />
                <span className="text-sm">{tt(opt)}</span>
              </label>
            );
          })}
        </div>
      )}
      {qtype === "text" && (
        <Textarea
          value={answer || ""}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Ihre Antwort\u2026"
          className="mt-3 rounded-[10px]"
          rows={3}
          data-testid={`quiz-q${qi}-text`}
        />
      )}
      {fb && fb.counts && (
        <p className={`mt-2 text-xs font-medium ${fb.correct ? "text-emerald-600" : "text-red-600"}`}>
          {fb.correct ? "Richtig beantwortet" : "Nicht korrekt"}
        </p>
      )}
    </div>
  );
}

/* ---------- Main player ---------- */

export default function CoursePlayer() {
  const { id } = useParams();
  const { t, tt } = useLang();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [certOpen, setCertOpen] = useState(false);
  const [checklistChecked, setChecklistChecked] = useState([]);

  const load = useCallback(() => {
    api.get(`/academy/courses/${id}`).then((r) => {
      setCourse(r.data);
      const lessons = r.data.modules.flatMap((m) => m.lessons);
      const done = r.data.enrollment?.completed_lessons || [];
      const next = lessons.find((l) => !done.includes(l.id)) || lessons[0];
      setActiveLesson((prev) => prev ? lessons.find((l) => l.id === prev.id) || next : next);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // restore checklist state when switching lessons
    if (activeLesson?.type === "checklist" && course) {
      const saved = course.enrollment?.lesson_state?.[activeLesson.id]?.checked || [];
      setChecklistChecked(saved);
    }
  }, [activeLesson?.id, course]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !course) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 rounded-[16px] lg:col-span-2" />
          <Skeleton className="h-96 rounded-[16px]" />
        </div>
      </div>
    );
  }

  const lessons = course.modules.flatMap((m) => m.lessons);
  const done = course.enrollment?.completed_lessons || [];
  const progressPct = lessons.length ? Math.round((done.length / lessons.length) * 100) : 0;
  const allLessonsDone = done.length >= lessons.length;
  const hasQuiz = course.quiz?.questions?.length > 0;
  const passScore = course.quiz?.pass_score ?? 70;
  const maxAttempts = course.quiz?.max_attempts;
  const attempts = course.enrollment?.quiz_attempts || 0;

  const completeLesson = async (lesson) => {
    try {
      const body = lesson.type === "checklist" ? { state: { checked: checklistChecked } } : {};
      const res = await api.post(`/academy/courses/${course.id}/lessons/${lesson.id}/complete`, body);
      load();
      toast.success(t("Lektion abgeschlossen", "Lesson completed"));
      if (res.data.completed && res.data.certificate_id) setCertOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    }
  };

  const submitQuiz = async () => {
    const qs = course.quiz.questions;
    const unanswered = qs.filter((q) => {
      const a = answers[q.id];
      if ((q.qtype || "single") === "multiple") return !Array.isArray(a) || a.length === 0;
      if ((q.qtype || "single") === "text") return !String(a || "").trim();
      return a === undefined || a === null;
    });
    if (unanswered.length) {
      toast.error(t("Bitte alle Fragen beantworten", "Please answer all questions"));
      return;
    }
    try {
      const res = await api.post(`/academy/courses/${course.id}/quiz`, { answers });
      setQuizResult(res.data);
      if (res.data.passed) {
        load();
        if (res.data.completed) setCertOpen(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Absenden", "Error submitting"));
    }
  };

  const renderLessonBody = (lesson) => {
    switch (lesson.type) {
      case "video":
        return <VideoLesson lesson={lesson} tt={tt} />;
      case "pdf":
      case "download":
        return <FileLesson lesson={lesson} t={t} tt={tt} />;
      case "link":
        return <LinkLesson lesson={lesson} t={t} tt={tt} />;
      case "checklist":
        return (
          <ChecklistLesson
            lesson={lesson}
            tt={tt}
            checked={checklistChecked}
            setChecked={setChecklistChecked}
            isDone={done.includes(lesson.id)}
          />
        );
      default:
        return (
          <div className="mt-5 text-sm sm:text-base leading-relaxed text-foreground/85 whitespace-pre-line">
            {tt(lesson.content)}
          </div>
        );
    }
  };

  const typeMeta = LESSON_TYPE_META[activeLesson?.type] || LESSON_TYPE_META.text;

  return (
    <div className="space-y-6" data-testid="course-player">
      <div>
        <Link to="/academy" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="course-back-link">
          <ChevronLeft className="h-4 w-4" /> Eleven Academy
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="rounded-full text-[10px]">{tt(CATEGORY_LABELS[course.category] || { de: course.category, en: course.category })}</Badge>
              {course.is_mandatory && <Badge variant="outline" className="rounded-full text-[10px] border-[#D6C08D]/50 text-[#A8905A]">{t("Pflicht", "Mandatory")}</Badge>}
              {course.enrollment?.completed && <Badge className="rounded-full text-[10px] bg-emerald-600">{t("Abgeschlossen", "Completed")}</Badge>}
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="course-title">{tt(course.title)}</h1>
          </div>
          <div className="flex items-center gap-3 min-w-[180px]">
            <Progress value={progressPct} className="h-2 flex-1" />
            <span className="font-mono-num text-sm text-muted-foreground">{progressPct}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-[16px] lg:col-span-2">
          <CardContent className="p-6 sm:p-8">
            {!quizMode && activeLesson && (
              <div data-testid="lesson-view">
                <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Clock3 className="h-3.5 w-3.5" /> {activeLesson.duration_minutes} min
                  <span className="inline-flex items-center gap-1"><typeMeta.icon className="h-3.5 w-3.5" /> {t(typeMeta.de, typeMeta.en)}</span>
                </p>
                <h2 className="mt-2 font-display text-2xl font-semibold">{tt(activeLesson.title)}</h2>
                {renderLessonBody(activeLesson)}
                <div className="mt-8 flex items-center justify-between">
                  {done.includes(activeLesson.id) ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                      <CircleCheck className="h-4 w-4" /> {t("Abgeschlossen", "Completed")}
                    </span>
                  ) : (
                    <Button onClick={() => completeLesson(activeLesson)} className="rounded-[12px] bg-[#0B1220]" data-testid="lesson-complete-button">
                      <CircleCheck className="h-4 w-4 mr-1.5" /> {t("Lektion abschlie\u00dfen", "Complete lesson")}
                    </Button>
                  )}
                  {(() => {
                    const idx = lessons.findIndex((l) => l.id === activeLesson.id);
                    const nxt = lessons[idx + 1];
                    return nxt ? (
                      <Button variant="outline" className="rounded-[12px]" onClick={() => setActiveLesson(nxt)} data-testid="lesson-next-button">
                        {t("N\u00e4chste Lektion", "Next lesson")}
                      </Button>
                    ) : hasQuiz && allLessonsDone && !course.enrollment?.quiz_passed ? (
                      <Button variant="outline" className="rounded-[12px] border-[#D6C08D]/50 text-[#A8905A]" onClick={() => setQuizMode(true)} data-testid="lesson-to-quiz-button">
                        <ClipboardCheck className="h-4 w-4 mr-1.5" /> {t("Zum Wissenstest", "To knowledge test")}
                      </Button>
                    ) : null;
                  })()}
                </div>
              </div>
            )}
            {quizMode && (
              <div data-testid="lesson-quiz">
                <h2 className="font-display text-2xl font-semibold">{t("Wissenstest", "Knowledge Test")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(`Bestehensgrenze: ${passScore}%`, `Passing threshold: ${passScore}%`)}
                  {" \u00b7 "}
                  {t(`Versuch ${attempts + (quizResult ? 0 : 1)}`, `Attempt ${attempts + (quizResult ? 0 : 1)}`)}
                  {maxAttempts ? t(` von ${maxAttempts}`, ` of ${maxAttempts}`) : ""}
                </p>
                {quizResult && (
                  <div className={`mt-4 rounded-[12px] border p-4 ${quizResult.passed ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="quiz-result">
                    <p className="text-sm font-semibold">
                      {quizResult.passed
                        ? t(`Bestanden mit ${quizResult.score}%`, `Passed with ${quizResult.score}%`)
                        : t(`Nicht bestanden (${quizResult.score}%). Versuchen Sie es erneut.`, `Not passed (${quizResult.score}%). Try again.`)}
                    </p>
                  </div>
                )}
                <div className="mt-6 space-y-6">
                  {course.quiz.questions.map((q, qi) => (
                    <QuizQuestion
                      key={q.id}
                      q={q}
                      qi={qi}
                      answer={answers[q.id]}
                      setAnswer={(v) => setAnswers({ ...answers, [q.id]: v })}
                      tt={tt}
                      result={quizResult}
                    />
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <Button variant="ghost" className="rounded-[10px]" onClick={() => { setQuizMode(false); setQuizResult(null); }}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> {t("Zur\u00fcck zu Lektionen", "Back to lessons")}
                  </Button>
                  {quizResult && !quizResult.passed ? (
                    <Button onClick={() => { setQuizResult(null); setAnswers({}); }} variant="outline" className="rounded-[12px] border-[#D6C08D]/50 text-[#A8905A]" data-testid="quiz-retry-button">
                      {t("Erneut versuchen", "Try again")}
                    </Button>
                  ) : !quizResult ? (
                    <Button onClick={submitQuiz} className="rounded-[12px] bg-[#0B1220]" data-testid="quiz-submit-button">
                      {t("Test absenden", "Submit test")}
                    </Button>
                  ) : null}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="rounded-[16px]">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold">{t("Kursinhalt", "Course content")}</h3>
              <div className="mt-4 space-y-4">
                {course.modules.map((m, mi) => (
                  <div key={m.id}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("Modul", "Module")} {mi + 1}: {tt(m.title)}
                    </p>
                    <div className="mt-2 space-y-1">
                      {m.lessons.map((l) => {
                        const Icon = (LESSON_TYPE_META[l.type] || LESSON_TYPE_META.text).icon;
                        return (
                          <button
                            key={l.id}
                            onClick={() => { setQuizMode(false); setActiveLesson(l); }}
                            className={`w-full flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-sm transition-colors ${
                              activeLesson?.id === l.id && !quizMode ? "bg-[#0B1220] text-white" : "hover:bg-muted"
                            }`}
                            data-testid={`lesson-nav-${l.id}`}
                          >
                            {done.includes(l.id) ? (
                              <CircleCheck className={`h-4 w-4 shrink-0 ${activeLesson?.id === l.id && !quizMode ? "text-[#D6C08D]" : "text-emerald-600"}`} />
                            ) : (
                              <Circle className="h-4 w-4 shrink-0 opacity-40" />
                            )}
                            <span className="truncate flex-1">{tt(l.title)}</span>
                            <Icon className="h-3.5 w-3.5 shrink-0 opacity-50" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {hasQuiz && (
                  <button
                    onClick={() => allLessonsDone ? setQuizMode(true) : toast.info(t("Bitte zuerst alle Lektionen abschlie\u00dfen", "Please complete all lessons first"))}
                    className={`w-full flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-sm transition-colors border ${
                      quizMode ? "bg-[#0B1220] text-white border-transparent" : allLessonsDone ? "border-[#D6C08D]/50 hover:bg-[#D6C08D]/10" : "border-border opacity-50"
                    }`}
                    data-testid="quiz-nav-button"
                  >
                    <ClipboardCheck className={`h-4 w-4 shrink-0 ${course.enrollment?.quiz_passed ? "text-emerald-500" : ""}`} />
                    {t("Wissenstest", "Knowledge test")}
                    {course.enrollment?.quiz_passed && <CircleCheck className="h-3.5 w-3.5 text-emerald-500 ml-auto" />}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
          {course.enrollment?.quiz_score !== null && course.enrollment?.quiz_score !== undefined && (
            <Card className="rounded-[16px]">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold">{t("Testergebnis", "Test result")}</h3>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("Letzter Versuch", "Last attempt")}</span>
                  <span className={`font-mono-num font-semibold ${course.enrollment.quiz_passed ? "text-emerald-600" : "text-red-600"}`}>
                    {course.enrollment.quiz_score}%
                  </span>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("Versuche", "Attempts")}</span>
                  <span className="font-mono-num">{attempts}{maxAttempts ? ` / ${maxAttempts}` : ""}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6" data-testid="course-certificate-dialog">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#D6C08D]/15 flex items-center justify-center">
              <Award className="h-7 w-7 text-[#A8905A]" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-semibold">{t("Herzlichen Gl\u00fcckwunsch!", "Congratulations!")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("Sie haben den Kurs abgeschlossen. Ihr Zertifikat finden Sie in Ihrem Profil.", "You have completed the course. You can find your certificate in your profile.")}
            </p>
            <Button asChild className="mt-6 rounded-[12px] bg-[#0B1220]" data-testid="certificate-to-profile">
              <Link to="/portal/profil">{t("Zum Profil", "To profile")}</Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
